import { useEffect, useMemo, useRef, useState } from 'react';

import { de } from '../../i18n/de';
import { TapToSpeakOffer } from '../../components/TapToSpeakButton';
import { useComposerStore } from '../../core/composer/useComposer';
import { useProfileStore } from '../../core/profile/ProfileStore';
import { useUiStore } from '../../app/uiStore';
import { ActivateButton } from '../../components/ActivateButton';

/**
 * Full-screen text, for turning the iPad toward the person opposite.
 *
 * It has two jobs, and the second is the reason it ships in Phase 1 rather than later:
 *
 *  1. **Deliberate use** — the person taps their own sentence to show it, rotates the screen 180°,
 *     and is read instead of heard. Useful in a noisy room, or when the voice is wrong for the
 *     moment.
 *  2. **The `VisualOnly` fallback** — when every speech provider has failed, `SpeechService` pushes
 *     this surface up automatically. Being read is worse than being heard and far better than
 *     being silent.
 *
 * When it was reached by failure rather than by tap, it says so. A person who thinks they were
 * heard and was not is worse off than one who knows to hold up the screen.
 */

/** Auto-fit: shrink until it fits rather than truncating. Every word matters here. */
function useFittedFontSize(text: string, containerRef: React.RefObject<HTMLDivElement | null>): number {
  const [size, setSize] = useState(160);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let candidate = 160;
    const measure = container.firstElementChild as HTMLElement | null;
    if (!measure) return;

    // Coarse downward search: cheap, deterministic, and runs once per text change.
    while (candidate > 24) {
      measure.style.fontSize = `${candidate}px`;
      if (measure.scrollHeight <= container.clientHeight && measure.scrollWidth <= container.clientWidth) {
        break;
      }
      candidate -= 8;
    }
    setSize(candidate);
  }, [text, containerRef]);

  return size;
}

export function PartnerAnsicht(): React.JSX.Element {
  const text = useComposerStore((s) => s.state.text);
  // Persisted (C60): which way the iPad faces is a fact about the room, not about this session — and `uiStore`
  // is deliberately the one store that survives nothing. Somebody who turned the screen towards the person
  // opposite should not have to do it again after every relaunch.
  const rotated = useProfileStore((s) => s.settings.partnerRotated);
  const updateSettings = useProfileStore((s) => s.update);
  const toggleRotation = (): void => void updateSettings({ partnerRotated: !rotated });
  const back = useUiStore((s) => s.back);
  const forcedBy = useUiStore((s) => s.forcedBy);

  const containerRef = useRef<HTMLDivElement>(null);
  const trimmed = useMemo(() => text.replace(/\s+/gu, ' ').trim(), [text]);
  const fontSize = useFittedFontSize(trimmed, containerRef);

  // "ich schreibe…" while the buffer has content but nothing has been shown yet — it tells the
  // partner to wait, which is the single most useful thing this screen can say (FR19).
  const display = trimmed === '' ? de.partnerWaiting : trimmed;

  return (
    <div
      data-testid="partner-ansicht"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg)',
        color: 'var(--fg)',
        transform: rotated ? 'rotate(180deg)' : 'none',
        padding: 'calc(var(--safe-top) + 8px) 16px calc(var(--safe-bottom) + 8px)',
      }}
    >
      {forcedBy === 'speechFailure' ? (
        <p
          data-testid="partner-failure-notice"
          style={{
            flex: '0 0 auto',
            padding: 8,
            fontSize: 'var(--font-button)',
            fontWeight: 700,
            textAlign: 'center',
            background: 'var(--danger)',
            color: 'var(--danger-fg)',
            border: '3px solid var(--fg)',
            borderRadius: 'var(--radius)',
          }}
        >
          {de.speechFailedVisualOnly}
        </p>
      ) : null}

      {/* The tap-to-speak offer belongs here as well as on Sprechen (3.2), and the flow suite is what
          showed why: `visualOnly` *navigates to this surface*, so a button living only in the prediction
          row could never be seen by the person it was offered to. Here it is in front of them, next to the
          sentence nobody heard. */}
      <TapToSpeakOffer />

      <div
        ref={containerRef}
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <span
          data-testid="partner-text"
          style={{
            fontSize,
            lineHeight: 1.1,
            fontWeight: 700,
            textAlign: 'center',
            overflowWrap: 'anywhere',
            color: trimmed === '' ? 'var(--muted)' : 'var(--fg)',
          }}
        >
          {display}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 'var(--key-gap)', flex: '0 0 auto' }}>
        <ActivateButton
          id="partner:rotate"
          testId="partner-rotate"
          onActivate={toggleRotation}
          style={partnerButton}
        >
          {de.rotate}
        </ActivateButton>
        <ActivateButton id="partner:back" testId="partner-back" onActivate={back} style={partnerButton}>
          {de.back}
        </ActivateButton>
      </div>
    </div>
  );
}

const partnerButton: React.CSSProperties = {
  flex: '1 1 0',
  minHeight: 'var(--min-target)',
  fontSize: 'var(--font-button)',
  background: 'var(--key-bg)',
  color: 'var(--key-fg)',
  border: '3px solid var(--key-border)',
  borderRadius: 'var(--radius)',
  transition: 'none',
};
